import React, { useEffect, useState } from 'react';
import {
  isPushSupported, isIos, isStandalone, permission,
  fetchPushConfig, getSubscription, enablePush, disablePush, syncPush
} from '../../utils/push';
import { MAX_REMINDER_HOURS } from '../../utils/goals';

const HOURS = Array.from({ length: 24 }, (unused, hour) => hour);
const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

/**
 * Push reminders. The delivering job runs once an hour, so only whole hours can
 * be honoured — 07:35 is not offered rather than silently rounded.
 */
const ReminderSettings = ({ hours, enabled, onChange, goalMetOn }) => {
  const [available, setAvailable] = useState(null); // null while unknown
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) { if (!cancelled) setAvailable(false); return; }
      const config = await fetchPushConfig();
      const subscription = await getSubscription();
      if (cancelled) return;
      setAvailable(Boolean(config.configured));
      setSubscribed(Boolean(subscription) && permission() === 'granted');
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleHour = (hour) => {
    const next = hours.includes(hour)
      ? hours.filter((value) => value !== hour)
      : [...hours, hour].sort((a, b) => a - b);

    if (next.length > MAX_REMINDER_HOURS) {
      setMessage(`Four reminders a day is the most you can set.`);
      return;
    }
    setMessage(null);
    onChange({ hours: next, enabled });
    if (subscribed) syncPush({ hours: next, enabled, goalMetOn });
  };

  const turnOn = async () => {
    setBusy(true);
    setMessage(null);
    const result = await enablePush({ hours, goalMetOn });
    setBusy(false);
    if (result.ok) {
      setSubscribed(true);
      onChange({ hours, enabled: true });
      setMessage('Reminders are on for this device.');
    } else {
      setMessage(result.reason);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    await disablePush();
    setBusy(false);
    setSubscribed(false);
    onChange({ hours, enabled: false });
    setMessage('Reminders are off for this device.');
  };

  const note = (text) => (
    <p className="text-sm font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.75 }}>{text}</p>
  );

  return (
    <div>
      <p className="text-sm mb-3 font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.75 }}>
        Pick up to {MAX_REMINDER_HOURS} times. Reminders arrive on the hour, and are skipped once
        you have met the goal for the day.
      </p>

      <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 mb-3">
        {HOURS.map((hour) => {
          const active = hours.includes(hour);
          return (
            <button
              key={hour}
              type="button"
              onClick={() => toggleHour(hour)}
              aria-pressed={active}
              className={`py-1.5 rounded-md text-sm font-lato border transition-colors ${
                active ? 'bg-button-bg text-button-text' : 'bg-light-gray text-charcoal hover:bg-light-gold'
              }`}
              style={{ borderColor: active ? 'var(--color-button-bg)' : 'var(--color-card-border)' }}
            >
              {formatHour(hour)}
            </button>
          );
        })}
      </div>

      <p className="text-xs mb-4 font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.6 }}>
        {hours.length === 0
          ? 'No times picked yet.'
          : `${hours.length} of ${MAX_REMINDER_HOURS} picked: ${hours.map(formatHour).join(', ')}`}
      </p>

      {available === false && note(
        isPushSupported()
          ? 'Notifications are not set up on this deployment yet. Your chosen times are saved and will be used once they are.'
          : 'This browser cannot deliver notifications.'
      )}

      {available && isIos() && !isStandalone() && note(
        'On iPhone and iPad, add this app to your home screen first — Safari only sends notifications to installed apps.'
      )}

      {available && (
        <div className="flex flex-wrap items-center gap-3">
          {subscribed ? (
            <button
              type="button" onClick={turnOff} disabled={busy}
              className="px-4 py-2 rounded-md bg-light-gray text-charcoal hover:bg-light-gold font-lato transition-colors disabled:opacity-50"
            >
              {busy ? 'Working…' : 'Turn off on this device'}
            </button>
          ) : (
            <button
              type="button" onClick={turnOn} disabled={busy || hours.length === 0}
              className="px-4 py-2 rounded-md bg-button-bg text-button-text hover:bg-medium-green font-lato transition-colors disabled:opacity-50"
            >
              {busy ? 'Working…' : 'Turn on notifications'}
            </button>
          )}
          <span className="text-sm font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.7 }}>
            {subscribed ? 'On for this device' : 'Off'}
          </span>
        </div>
      )}

      {message && (
        <p className="text-sm mt-3 font-lato" role="status" style={{ color: 'var(--color-charcoal)' }}>{message}</p>
      )}
    </div>
  );
};

export default ReminderSettings;
